import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import { errMsg } from "@/lib/errors";
import {
  Wrench, LayoutDashboard, Inbox, Users, Store, CalendarClock, CreditCard,
  Undo2, Star, Tag, AlertTriangle, ShieldCheck, LogOut, Activity, Search,
} from "lucide-react";

const OWNER_TABS = [
  { key: "overview",   label: "Overview",           icon: LayoutDashboard },
  { key: "leads",      label: "Leads",              icon: Inbox },
  { key: "customers",  label: "Customers",          icon: Users },
  { key: "providers",  label: "Providers",          icon: Store },
  { key: "bookings",   label: "Bookings",           icon: CalendarClock },
  { key: "payments",   label: "Payments",           icon: CreditCard },
  { key: "refunds",    label: "Refunds",            icon: Undo2 },
  { key: "reviews",    label: "Reviews",            icon: Star },
  { key: "categories", label: "Categories",         icon: Tag },
  { key: "disputes",   label: "Disputes",           icon: AlertTriangle },
];

const STATUS_PILL = {
  requested: "bg-slate-200 text-slate-800",
  quoted: "bg-amber-200 text-amber-900",
  accepted: "bg-blue-200 text-blue-900",
  paid: "bg-emerald-200 text-emerald-900",
  in_progress: "bg-indigo-200 text-indigo-900",
  completed: "bg-emerald-600 text-white",
  declined: "bg-rose-200 text-rose-900",
  cancelled: "bg-rose-200 text-rose-900",
  refunded: "bg-purple-200 text-purple-900",
  new: "bg-amber-200 text-amber-900",
  contacted: "bg-blue-200 text-blue-900",
  converted: "bg-emerald-600 text-white",
  archived: "bg-slate-300 text-slate-700",
};

function KpiCard({ label, value, sub, tone = "slate" }) {
  const toneMap = {
    slate: "bg-slate-900 text-white",
    emerald: "bg-emerald-600 text-white",
    rose: "bg-rose-600 text-white",
    amber: "bg-amber-500 text-white",
    indigo: "bg-indigo-600 text-white",
  };
  return (
    <div className={`${toneMap[tone]} rounded-2xl p-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]`}>
      <div className="text-[11px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-3xl font-display font-bold mt-1">{value}</div>
      {sub && <div className="text-xs opacity-80 mt-1">{sub}</div>}
    </div>
  );
}

function Row({ children, testId, onClick }) {
  return (
    <div data-testid={testId} onClick={onClick}
      className={`bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-start justify-between gap-3 ${onClick ? "cursor-pointer hover:border-slate-400" : ""}`}>
      {children}
    </div>
  );
}

function Pill({ children, className = "" }) {
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${className}`}>{children}</span>;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [q, setQ] = useState("");
  const [newCat, setNewCat] = useState({ slug: "", name: "", description: "", sort_order: 100 });
  const [stripeCfg, setStripeCfg] = useState(null);
  // Preview-only "View LIVE data" toggle for the Providers tab. When ON, we
  // fetch a read-only mirror of production's provider list via the
  // /admin/users_live server-side proxy (never writes anywhere). Buttons are
  // disabled in this mode so nobody can accidentally act on production rows.
  const [liveProviders, setLiveProviders] = useState(false);
  const [liveProvidersData, setLiveProvidersData] = useState(null);
  const [liveProvidersError, setLiveProvidersError] = useState("");
  const [liveProvidersLoading, setLiveProvidersLoading] = useState(false);

  const loadLiveProviders = async () => {
    setLiveProvidersLoading(true);
    setLiveProvidersError("");
    try {
      const { data } = await api.get("/admin/users_live", { params: { role: "provider" } });
      setLiveProvidersData(data || []);
    } catch (e) {
      setLiveProvidersData(null);
      setLiveProvidersError(errMsg(e, "Could not load LIVE production providers"));
    } finally {
      setLiveProvidersLoading(false);
    }
  };

  const load = async () => {
    try {
      const [s, act, c, p, b, l, pay, rv, cats, disp, sc] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/activity"),
        api.get("/admin/users", { params: { role: "customer" } }),
        api.get("/admin/users", { params: { role: "provider" } }),
        api.get("/admin/bookings"),
        api.get("/admin/leads"),
        api.get("/admin/payments"),
        api.get("/admin/reviews"),
        api.get("/admin/categories"),
        api.get("/admin/disputes"),
        api.get("/admin/stripe/config").catch(() => ({ data: null })),
      ]);
      setStats(s.data); setActivity(act.data); setCustomers(c.data); setProviders(p.data);
      setBookings(b.data); setLeads(l.data); setPayments(pay.data); setReviews(rv.data);
      setCategories(cats.data); setDisputes(disp.data); setStripeCfg(sc.data);
    } catch (err) {
      toast.error(errMsg(err, "Failed to load admin data"));
    }
  };
  useEffect(() => { load(); }, []);

  const suspend = async (uid) => { try { await api.post(`/admin/users/${uid}/suspend`); toast.success("Updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const verify = async (pid) => { try { await api.post(`/admin/providers/${pid}/verify`); toast.success("Updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const setLeadStatus = async (lid, status) => { try { await api.post(`/admin/leads/${lid}/status`, { status, admin_note: "" }); toast.success("Lead updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const refund = async (session_id) => {
    if (!window.confirm("Refund this payment? This cannot be undone.")) return;
    try { await api.post(`/admin/payments/${session_id}/refund`); toast.success("Refund issued"); load(); }
    catch (e) { toast.error(errMsg(e, "Refund failed")); }
  };
  const resolveDispute = async (bid) => { try { await api.post(`/admin/disputes/${bid}/resolve`); toast.success("Dispute resolved"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const toggleCategory = async (id, field, val) => { try { await api.patch(`/admin/categories/${id}`, { [field]: val }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const createCategory = async () => {
    try {
      await api.post("/admin/categories", { ...newCat, sort_order: Number(newCat.sort_order) || 100 });
      setNewCat({ slug: "", name: "", description: "", sort_order: 100 });
      toast.success("Category created");
      load();
    } catch (e) { toast.error(errMsg(e, "Failed to create")); }
  };

  const filterText = q.trim().toLowerCase();
  const filterFn = (obj) => !filterText || JSON.stringify(obj).toLowerCase().includes(filterText);

  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster position="top-center" />

      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-60 bg-slate-950 text-slate-200 hidden md:flex md:flex-col z-30">
        <div className="px-5 py-5 border-b border-slate-800 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500 text-white">
            <Wrench size={18} />
          </span>
          <div>
            <div className="font-display font-extrabold text-white">FixNear</div>
            <div className="text-[10px] tracking-widest uppercase text-emerald-400">Owner Control</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {OWNER_TABS.map((t) => {
            const Ico = t.icon;
            const active = tab === t.key;
            const badge =
              t.key === "leads" && stats.new_leads ? stats.new_leads
              : t.key === "providers" && stats.pending_approvals ? stats.pending_approvals
              : t.key === "disputes" && stats.open_disputes ? stats.open_disputes
              : null;
            return (
              <button key={t.key} data-testid={`owner-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors
                  ${active ? "bg-emerald-500 text-white" : "text-slate-300 hover:bg-slate-800"}`}>
                <Ico size={17} /> <span className="flex-1 text-left">{t.label}</span>
                {badge != null && <span className="bg-rose-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5">{badge}</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="text-xs text-slate-400 mb-2 px-2">Signed in as</div>
          <div className="px-2 mb-2">
            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
          </div>
          <button data-testid="owner-logout" onClick={() => { logout(); window.location.href = "/"; }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-rose-600 text-sm font-semibold text-white">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="md:pl-60">
        {/* Top bar */}
        <div className="bg-white border-b border-slate-200 px-5 py-4 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-slate-900 text-white">Owner</span>
            <h1 data-testid="owner-heading" className="font-display text-xl font-bold text-slate-900 capitalize">{tab === "overview" ? "Owner Control Panel" : tab}</h1>
          </div>
          <div className="flex-1 min-w-[200px] relative">
            {["customers","providers","bookings","leads","payments","reviews","categories","disputes"].includes(tab) && (
              <>
                <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                <input data-testid="owner-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm bg-slate-50" />
              </>
            )}
          </div>
        </div>

        {/* Mobile role tabs */}
        <div className="md:hidden bg-slate-950 text-slate-200 overflow-x-auto flex gap-1 px-3 py-2">
          {OWNER_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold ${tab === t.key ? "bg-emerald-500 text-white" : "bg-slate-800 text-slate-300"}`}>{t.label}</button>
          ))}
        </div>

        <main className="p-5 space-y-5">
          {/* OVERVIEW */}
          {tab === "overview" && (
            <>
              {stripeCfg && (
                <div data-testid="stripe-mode-banner" className={`rounded-2xl p-4 border ${stripeCfg.connect_ready && stripeCfg.mode === "live" ? "bg-emerald-50 border-emerald-200" : stripeCfg.mode === "live" ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200"}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Stripe wiring</div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <Pill className={stripeCfg.mode === "live" ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}>{stripeCfg.mode?.toUpperCase() || "UNKNOWN"}</Pill>
                        <span className="text-sm text-slate-800">Commission <b>{stripeCfg.commission_pct}%</b> · Currency <b>{(stripeCfg.currency || "gbp").toUpperCase()}</b></span>
                        <Pill className={stripeCfg.webhook_configured ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}>Webhook {stripeCfg.webhook_configured ? "✓" : "✗"}</Pill>
                        <Pill className={stripeCfg.connect_ready ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"} data-testid="connect-ready-pill">
                          Connect {stripeCfg.connect_ready ? "ready ✓" : "not ready ✗"}
                        </Pill>
                        {stripeCfg.platform_account?.charges_enabled ? <Pill className="bg-emerald-100 text-emerald-800">Platform charges ✓</Pill> : <Pill className="bg-rose-100 text-rose-800">Platform charges ✗</Pill>}
                        {stripeCfg.platform_account?.payouts_enabled ? <Pill className="bg-emerald-100 text-emerald-800">Platform payouts ✓</Pill> : <Pill className="bg-rose-100 text-rose-800">Platform payouts ✗</Pill>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Provider Connect: <b>{stripeCfg.payout_ready_providers}</b> payout-ready · <b>{stripeCfg.connected_providers}</b> connected accounts
                        {stripeCfg.platform_account?.id && <> · Platform <code>{stripeCfg.platform_account.id}</code></>}
                      </div>
                      {stripeCfg.config_issues?.length > 0 && (
                        <ul className="mt-2 text-xs text-rose-700 space-y-0.5 list-disc list-inside" data-testid="stripe-config-issues">
                          {stripeCfg.config_issues.map((issue, i) => (<li key={i}>{issue}</li>))}
                        </ul>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {!stripeCfg.connect_ready && stripeCfg.platform_setup_url && (
                        <a href={stripeCfg.platform_setup_url} target="_blank" rel="noreferrer" data-testid="complete-connect-setup-btn"
                          className="inline-flex items-center gap-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-sm font-bold">
                          Complete Connect setup →
                        </a>
                      )}
                      {stripeCfg.mode === "test" && (
                        <div className="text-xs font-semibold text-amber-800 max-w-xs text-right">Test mode — no real money moves. Rotate Stripe keys to <code>sk_live_</code> in Secrets when ready.</div>
                      )}
                      {stripeCfg.mode === "live" && stripeCfg.connect_ready && (
                        <div className="text-xs font-semibold text-emerald-800 max-w-xs text-right">Live — ready to accept real customer payments.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard tone="slate"   label="Customers"           value={stats.customers ?? 0} />
                <KpiCard tone="slate"   label="Providers"           value={stats.providers ?? 0} sub={`${stats.pending_approvals ?? 0} pending approval`} />
                <KpiCard tone="amber"   label="New leads"           value={stats.new_leads ?? 0} sub={`${stats.total_leads ?? 0} total leads`} />
                <KpiCard tone="indigo"  label="Active bookings"     value={stats.active_bookings ?? 0} sub={`${stats.completed ?? 0} completed`} />
                <KpiCard tone="emerald" label="Gross sales"         value={`£${(stats.gross_revenue ?? 0).toFixed(2)}`} sub={`${stats.bookings ?? 0} bookings total`} />
                <KpiCard tone="emerald" label="FixNear commission"  value={`£${(stats.commission_earned ?? 0).toFixed(2)}`} sub="15% platform fee" />
                <KpiCard tone="slate"   label="Provider payouts"    value={`£${(stats.provider_payouts ?? 0).toFixed(2)}`} sub="85% share" />
                <KpiCard tone="rose"    label="Refunds"             value={`£${(stats.refunds_total ?? 0).toFixed(2)}`} sub={`${stats.refunds_count ?? 0} issued · ${stats.open_disputes ?? 0} open disputes`} />
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Activity size={18} className="text-emerald-600" />
                  <h2 className="font-display text-lg font-bold">Recent marketplace activity</h2>
                </div>
                <ol className="space-y-2">
                  {activity.length === 0 && <li className="text-sm text-slate-500">No activity yet.</li>}
                  {activity.map((a, i) => (
                    <li key={i} data-testid={`activity-item-${i}`} className="flex items-start gap-3 text-sm border-b border-slate-100 last:border-0 py-2">
                      <Pill className={
                        a.type === "lead" ? "bg-amber-100 text-amber-800" :
                        a.type === "booking" ? "bg-indigo-100 text-indigo-800" :
                        a.type === "signup" ? "bg-slate-200 text-slate-800" :
                        "bg-emerald-100 text-emerald-800"
                      }>{a.type}</Pill>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-800">{a.text}</div>
                        <div className="text-[11px] text-slate-500">{new Date(a.at).toLocaleString("en-GB")}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </>
          )}

          {/* LEADS */}
          {tab === "leads" && (
            <div className="space-y-3">
              {leads.filter(filterFn).length === 0 && <div className="bg-white p-6 rounded-2xl border border-slate-200 text-slate-600">No leads.</div>}
              {leads.filter(filterFn).map((l) => (
                <Row key={l.id} testId={`admin-lead-${l.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{l.name} · <span className="text-slate-500 font-normal">{l.category}</span></div>
                    <div className="text-sm text-slate-600 mt-0.5">{l.email} · {l.phone} · {l.postcode}</div>
                    <div className="text-sm text-slate-700 mt-2 line-clamp-3">{l.description}</div>
                    <div className="text-xs text-slate-500 mt-2">When: {l.when_needed} · {new Date(l.created_at).toLocaleString("en-GB")}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Pill className={STATUS_PILL[l.status]}>{l.status}</Pill>
                    <div className="flex gap-2 flex-wrap">
                      {l.status !== "contacted" && <button data-testid={`lead-mark-contacted-${l.id}`} onClick={() => setLeadStatus(l.id, "contacted")} className="btn-outline !py-1 !px-3 text-xs">Contacted</button>}
                      {l.status !== "converted" && <button data-testid={`lead-mark-converted-${l.id}`} onClick={() => setLeadStatus(l.id, "converted")} className="btn-accent !py-1 !px-3 text-xs">Converted</button>}
                      {l.status !== "archived" && <button data-testid={`lead-archive-${l.id}`} onClick={() => setLeadStatus(l.id, "archived")} className="btn-outline !py-1 !px-3 text-xs">Archive</button>}
                    </div>
                  </div>
                </Row>
              ))}
            </div>
          )}

          {/* CUSTOMERS */}
          {tab === "customers" && (
            <div className="space-y-3">
              {customers.filter(filterFn).map((u) => (
                <Row key={u.id} testId={`admin-customer-${u.id}`}>
                  <div>
                    <div className="font-semibold text-slate-900">{u.name} {u.suspended && <Pill className="bg-rose-100 text-rose-700 ml-2">SUSPENDED</Pill>}</div>
                    <div className="text-sm text-slate-600">{u.email} · {u.phone} · {u.postcode}</div>
                    <div className="text-xs text-slate-500 mt-1">Joined {new Date(u.created_at).toLocaleString("en-GB")}</div>
                  </div>
                  <button onClick={() => suspend(u.id)} className="btn-outline !py-1.5 !px-3 text-xs">{u.suspended ? "Unsuspend" : "Suspend"}</button>
                </Row>
              ))}
            </div>
          )}

          {/* PROVIDERS with pending queue on top */}
          {tab === "providers" && (
            <>
              {/* Read-only data-source toggle (Preview only). Lets QA compare
                  the Preview DB against the live production admin list without
                  ever touching production records. */}
              <div className="mb-3 flex items-center gap-3 flex-wrap">
                <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Data source</span>
                <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold">
                  <button
                    data-testid="providers-src-preview"
                    onClick={() => setLiveProviders(false)}
                    className={`px-3 py-1.5 rounded-full ${!liveProviders ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}
                  >Preview DB</button>
                  <button
                    data-testid="providers-src-live"
                    onClick={() => { setLiveProviders(true); if (!liveProvidersData) loadLiveProviders(); }}
                    className={`px-3 py-1.5 rounded-full ${liveProviders ? "bg-emerald-600 text-white shadow" : "text-slate-600"}`}
                  >LIVE production (read-only)</button>
                </div>
                {liveProviders && (
                  <button data-testid="providers-live-refresh" onClick={loadLiveProviders} className="text-xs text-emerald-700 font-semibold">Refresh</button>
                )}
                {liveProviders && (
                  <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    Read-only mirror of fixnear.co.uk · action buttons disabled
                  </span>
                )}
              </div>
              {liveProviders && liveProvidersLoading && (
                <div className="mb-3 text-sm text-slate-600" data-testid="providers-live-loading">Loading live production providers…</div>
              )}
              {liveProviders && liveProvidersError && (
                <div className="mb-3 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="providers-live-error">
                  {liveProvidersError}
                </div>
              )}
              {(() => {
                const source = liveProviders ? (liveProvidersData || []) : providers;
                const pending = source.filter((u) => u.provider && !u.provider.verified);
                return pending.length ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-3">
                    <div className="font-bold text-amber-900 mb-2 flex items-center gap-2 px-1"><ShieldCheck size={16} /> Pending approvals ({pending.length}){liveProviders ? " · LIVE" : ""} — review every detail below before approving</div>
                    {/* Compact one-liner replaced with the same full card as the main list,
                        so every pending provider shows Phone / Trade / Postcode / Registered
                        / KYC / Approval / Bio / Rate / Photo / Provider ID — never a stub. */}
                    <div className="space-y-3">
                      {pending.filter(filterFn).map((u) => <ProviderCard key={u.id} u={u} liveMode={liveProviders} verify={verify} suspend={suspend} highlightPending />)}
                    </div>
                  </div>
                ) : null;
              })()}
              <div className="space-y-3">
                {(liveProviders ? (liveProvidersData || []) : providers)
                  .filter(filterFn)
                  .filter((u) => !(u.provider && !u.provider.verified))
                  .map((u) => <ProviderCard key={u.id} u={u} liveMode={liveProviders} verify={verify} suspend={suspend} />)}
              </div>
            </>
          )}

          {/* BOOKINGS */}
          {tab === "bookings" && (
            <div className="space-y-3">
              {bookings.filter(filterFn).map((b) => (
                <Row key={b.id} testId={`admin-booking-${b.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{b.customer_name} → {b.provider_name}</div>
                    <div className="text-sm text-slate-600 line-clamp-2">{b.description}</div>
                    <div className="text-xs text-slate-500 mt-1">{b.preferred_date} · {b.postcode} · £{b.quote_amount || "—"} {b.dispute_flagged && <Pill className="bg-rose-100 text-rose-700 ml-2">Dispute</Pill>}</div>
                  </div>
                  <Pill className={STATUS_PILL[b.status] || "bg-slate-100 text-slate-700"}>{b.status}</Pill>
                </Row>
              ))}
            </div>
          )}

          {/* PAYMENTS */}
          {tab === "payments" && (
            <div className="space-y-3">
              {payments.filter(filterFn).map((t) => (
                <Row key={t.session_id} testId={`admin-payment-${t.session_id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">£{(t.amount || 0).toFixed(2)} · {t.currency?.toUpperCase()}</div>
                    <div className="text-xs text-slate-500">Session {t.session_id?.slice(0, 24)}…</div>
                    <div className="text-xs text-slate-500">PI: {t.stripe_payment_intent_id || "—"}</div>
                    <div className="text-xs text-slate-500">Booking: {t.booking_id?.slice(0, 8)} · Connect: {t.connect_enabled ? "yes" : "no"}</div>
                    <div className="text-xs text-slate-500">Created {new Date(t.created_at).toLocaleString("en-GB")}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Pill className={t.status === "refunded" ? "bg-purple-200 text-purple-900" : t.payment_status === "paid" ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-800"}>{t.status === "refunded" ? "refunded" : t.payment_status}</Pill>
                    {t.payment_status === "paid" && t.status !== "refunded" && (
                      <button data-testid={`admin-refund-${t.session_id}`} onClick={() => refund(t.session_id)} className="btn-outline !py-1.5 !px-3 text-xs">Refund</button>
                    )}
                  </div>
                </Row>
              ))}
              {payments.length === 0 && <div className="bg-white p-6 rounded-2xl border border-slate-200 text-slate-600">No Stripe transactions yet.</div>}
            </div>
          )}

          {/* REFUNDS (view-only, filtered from payments) */}
          {tab === "refunds" && (
            <div className="space-y-3">
              {payments.filter((t) => t.status === "refunded").filter(filterFn).map((t) => (
                <Row key={t.session_id} testId={`admin-refund-row-${t.session_id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">£{(t.amount || 0).toFixed(2)} refunded</div>
                    <div className="text-xs text-slate-500">Refund id {t.refund_id} · at {new Date(t.refunded_at || t.updated_at).toLocaleString("en-GB")}</div>
                    <div className="text-xs text-slate-500">Booking {t.booking_id}</div>
                  </div>
                  <Pill className="bg-purple-200 text-purple-900">refunded</Pill>
                </Row>
              ))}
              {payments.filter((t) => t.status === "refunded").length === 0 && <div className="bg-white p-6 rounded-2xl border border-slate-200 text-slate-600">No refunds issued.</div>}
            </div>
          )}

          {/* REVIEWS */}
          {tab === "reviews" && (
            <div className="space-y-3">
              {reviews.filter(filterFn).map((r) => (
                <Row key={r.id} testId={`admin-review-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{r.customer_name} · {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
                    <div className="text-sm text-slate-700 mt-1">{r.comment}</div>
                    <div className="text-xs text-slate-500 mt-1">Provider {r.provider_id?.slice(0, 8)} · {new Date(r.created_at).toLocaleString("en-GB")}</div>
                  </div>
                </Row>
              ))}
              {reviews.length === 0 && <div className="bg-white p-6 rounded-2xl border border-slate-200 text-slate-600">No reviews yet.</div>}
            </div>
          )}

          {/* CATEGORIES */}
          {tab === "categories" && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="font-bold mb-3">Add new category</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <input data-testid="cat-slug" placeholder="slug (e.g. tiling)" value={newCat.slug} onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })} className="field" />
                  <input data-testid="cat-name" placeholder="Name" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="field" />
                  <input data-testid="cat-desc" placeholder="Description" value={newCat.description} onChange={(e) => setNewCat({ ...newCat, description: e.target.value })} className="field" />
                  <input data-testid="cat-order" type="number" placeholder="Sort" value={newCat.sort_order} onChange={(e) => setNewCat({ ...newCat, sort_order: e.target.value })} className="field" />
                </div>
                <button data-testid="cat-create" onClick={createCategory} disabled={!newCat.slug || !newCat.name} className="btn-accent mt-3">Add category</button>
              </div>
              <div className="space-y-2">
                {categories.filter(filterFn).map((c) => (
                  <Row key={c.id} testId={`admin-category-${c.slug}`}>
                    <div>
                      <div className="font-semibold text-slate-900">{c.name} <span className="text-slate-500 font-normal">/ {c.slug}</span></div>
                      <div className="text-xs text-slate-500">Sort {c.sort_order} · {c.active ? "Active" : "Inactive"} · {c.featured ? "Featured" : "Not featured"}</div>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`cat-toggle-featured-${c.slug}`} onClick={() => toggleCategory(c.id, "featured", !c.featured)} className="btn-outline !py-1.5 !px-3 text-xs">{c.featured ? "Unfeature" : "Feature"}</button>
                      <button data-testid={`cat-toggle-active-${c.slug}`} onClick={() => toggleCategory(c.id, "active", !c.active)} className="btn-outline !py-1.5 !px-3 text-xs">{c.active ? "Deactivate" : "Activate"}</button>
                    </div>
                  </Row>
                ))}
              </div>
            </div>
          )}

          {/* DISPUTES */}
          {tab === "disputes" && (
            <div className="space-y-3">
              {disputes.filter(filterFn).map((b) => (
                <Row key={b.id} testId={`admin-dispute-${b.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{b.customer_name} → {b.provider_name}</div>
                    <div className="text-sm text-slate-700 mt-1">Reason: {b.dispute_reason || "—"}</div>
                    <div className="text-xs text-slate-500 mt-1">Raised by: {b.dispute_raised_by} · {b.preferred_date} · £{b.quote_amount || "—"}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Pill className={b.dispute_resolved ? "bg-emerald-600 text-white" : "bg-rose-200 text-rose-900"}>{b.dispute_resolved ? "resolved" : "open"}</Pill>
                    {!b.dispute_resolved && <button data-testid={`dispute-resolve-${b.id}`} onClick={() => resolveDispute(b.id)} className="btn-accent !py-1.5 !px-3 text-xs">Mark resolved</button>}
                  </div>
                </Row>
              ))}
              {disputes.length === 0 && <div className="bg-white p-6 rounded-2xl border border-slate-200 text-slate-600">No disputes.</div>}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}


// ------------------------------------------------------------------------
// ProviderCard — a single review card used by BOTH the "Pending approvals"
// callout and the main provider list. Renders every LIVE field currently
// stored in FixNear for a provider so admin can review before approving.
// Every missing value falls back to "Not provided" and no click here ever
// runs unless the operator explicitly presses Approve/Suspend on Preview DB.
// ------------------------------------------------------------------------
function ProviderCard({ u, liveMode, verify, suspend, highlightPending }) {
  const p = u.provider || {};
  const na = (v) => (v === undefined || v === null || v === "" ? "Not provided" : v);
  const fmtDT = (iso) => {
    if (!iso) return "Not provided";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return String(iso); }
  };
  const cats = Array.isArray(p.categories) && p.categories.length ? p.categories.join(", ") : "Not provided";
  const postcodes = Array.isArray(p.postcodes) && p.postcodes.length ? p.postcodes.join(", ") : "Not provided";
  const signupPostcode = na(u.postcode);
  const rate = (typeof p.hourly_rate === "number" && p.hourly_rate > 0) ? `£${p.hourly_rate.toFixed(2)}/hr` : "Not provided";
  const ratingLabel = (typeof p.rating_avg === "number" && p.rating_avg > 0)
    ? `${p.rating_avg.toFixed(1)} ★ (${p.review_count || 0} reviews)`
    : `No reviews yet${p.review_count ? ` (${p.review_count})` : ""}`;
  const approved = !!p.verified;
  // KYC / identity verification — the only KYC signal FixNear currently
  // holds is Stripe Connect onboarding state. Present it plainly so an
  // approver knows whether identity + payout have been confirmed.
  const kycState = p.stripe_payouts_enabled
    ? { label: "Verified (KYC · payouts)", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" }
    : p.stripe_charges_enabled
      ? { label: "KYC in review", cls: "bg-blue-100 text-blue-800 border-blue-200" }
      : p.stripe_details_submitted
        ? { label: "KYC submitted", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" }
        : p.stripe_account_id
          ? { label: "KYC incomplete", cls: "bg-amber-100 text-amber-900 border-amber-200" }
          : { label: "KYC not started", cls: "bg-slate-100 text-slate-700 border-slate-200" };
  const approvalState = approved
    ? { label: "Approved", cls: "bg-emerald-600 text-white border-emerald-700" }
    : { label: "Pending Approval", cls: "bg-amber-500 text-white border-amber-600" };
  const stripeAcct = p.stripe_account_id ? `${p.stripe_account_id.slice(0, 8)}…${p.stripe_account_id.slice(-4)}` : "Not started";
  const initials = (u.name || p.business_name || "?").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div
      data-testid={`admin-provider-${u.id}`}
      className={`bg-white rounded-2xl border p-4 flex flex-col gap-4 md:flex-row md:items-start md:gap-4 ${highlightPending ? "border-amber-300" : "border-slate-200"}`}
    >
      {/* Avatar / photo */}
      <div className="shrink-0">
        {p.photo_url ? (
          <img src={p.photo_url} alt="" className="w-16 h-16 rounded-2xl object-cover border border-slate-200" />
        ) : (
          <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-800 font-bold text-xl flex items-center justify-center">
            {initials || "?"}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="min-w-0 flex-1">
        {/* Header line: full name · business name · status pills */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-semibold text-slate-900 text-base">{u.name || "Not provided"}</div>
          {p.business_name && p.business_name !== u.name && (
            <span className="text-xs text-slate-500">· {p.business_name}</span>
          )}
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border ${approvalState.cls}`} data-testid={`admin-provider-approval-${u.id}`}>{approvalState.label}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border ${kycState.cls}`} data-testid={`admin-provider-kyc-${u.id}`}>{kycState.label}</span>
          {u.suspended && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border border-rose-200 bg-rose-100 text-rose-700">SUSPENDED</span>}
        </div>

        {/* Field grid — the five required signup details FIRST so an
            approver can review the essentials at a glance, followed by any
            extra optional context (bio, rate, timestamps, IDs). */}
        <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50/40 p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800 mb-2">Signup details (required for approval)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-800">
            <div><span className="text-slate-500">Full name:</span> <span data-testid={`admin-provider-name-${u.id}`}>{na(u.name)}</span></div>
            <div><span className="text-slate-500">Phone:</span> <span data-testid={`admin-provider-phone-${u.id}`}>{na(u.phone)}</span></div>
            <div><span className="text-slate-500">Email:</span> <span data-testid={`admin-provider-email-${u.id}`}>{na(u.email)}</span></div>
            <div><span className="text-slate-500">Service / Trade:</span> <span data-testid={`admin-provider-trade-${u.id}`}>{cats}</span></div>
            <div className="sm:col-span-2"><span className="text-slate-500">Postcode / working area:</span> <span data-testid={`admin-provider-postcode-${u.id}`}>{postcodes !== "Not provided" ? postcodes : signupPostcode}</span></div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
          <div><span className="text-slate-500">Business name:</span> {na(p.business_name)}</div>
          <div><span className="text-slate-500">Hourly rate:</span> {rate}</div>
          <div><span className="text-slate-500">Registered:</span> {fmtDT(u.created_at)}</div>
          <div><span className="text-slate-500">Provider record:</span> {fmtDT(p.created_at)}</div>
          <div className="sm:col-span-2"><span className="text-slate-500">Bio:</span> {na(p.bio)}</div>
          <div><span className="text-slate-500">Stripe / KYC account:</span> <span className="font-mono text-[12px]">{stripeAcct}</span></div>
          <div><span className="text-slate-500">Rating &amp; reviews:</span> {ratingLabel}</div>
          <div><span className="text-slate-500">Provider ID:</span> <span className="font-mono text-[11px] text-slate-500">{na(p.id)}</span></div>
          <div><span className="text-slate-500">User ID:</span> <span className="font-mono text-[11px] text-slate-500">{na(u.id)}</span></div>
        </div>

        {/* Verification / KYC breakdown (Stripe Connect) — plain checkmarks
            so the reviewer can see exactly which identity steps have and
            have not been completed. Purely informational. */}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-slate-600">
          <span className="text-slate-500 uppercase tracking-widest text-[10px] font-bold">KYC breakdown:</span>
          <span>{p.stripe_details_submitted ? "✅" : "◻︎"} Details submitted</span>
          <span>{p.stripe_charges_enabled ? "✅" : "◻︎"} Charges enabled</span>
          <span>{p.stripe_payouts_enabled ? "✅" : "◻︎"} Payouts enabled</span>
        </div>
      </div>

      {/* Actions — disabled entirely in LIVE (read-only) mode. */}
      <div className="flex gap-2 flex-wrap shrink-0 md:flex-col md:items-end">
        {liveMode ? (
          <span className="text-[11px] text-slate-500 italic">Actions disabled — use fixnear.co.uk</span>
        ) : (
          <>
            {u.provider && (
              <button
                data-testid={`admin-provider-approve-${u.id}`}
                onClick={() => verify(u.provider.id)}
                className={approved ? "btn-outline !py-1.5 !px-3 text-xs" : "btn-accent !py-1.5 !px-3 text-xs"}
              >
                {approved ? "Unapprove" : "Approve"}
              </button>
            )}
            <button
              data-testid={`admin-provider-suspend-${u.id}`}
              onClick={() => suspend(u.id)}
              className="btn-outline !py-1.5 !px-3 text-xs"
            >
              {u.suspended ? "Unsuspend" : "Suspend"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
